/* eslint-disable @typescript-eslint/no-explicit-any */

import { imageDomain, imageDomainCN } from '../../modules/utils/constants';
import { checkChinaLocale, prependDomainIfMissing } from '../../modules/utils/helper';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const mapper = require('object-mapper');

const buildOfferDescIndex = (root: any) =>
  (root?.data?.marketing?.promotions?.customerPromotions?.offersDescriptions ?? []).reduce(
    (acc: Record<string, Array<{ key: string; value: string }>>, rec: any) => {
      const id = String(rec?.id || '').split('.')[0];
      if (id) acc[id] = (rec?.descriptions ?? []).map((d: any) => ({ key: d?.key, value: d?.value }));
      return acc;
    },
    {}
  );

const buildPromoDescIndex = (cp: any) =>
  (cp?.descriptions ?? []).reduce((acc: Record<string, string | null>, rec: any) => {
    const promoId = String(rec?.id || '').split('.')[0]; // e.g., "SH26.descriptions" -> "SH26"
    if (promoId) acc[promoId] = rec?.name ?? null;
    return acc;
  }, {});

const pickPrimary = (list: Array<{ key: string; value: string }> = []) => list.find(d => d?.value)?.value || null;

const imagesFromMedia = (m: any, locale: string) => {
  if (!m) return [];
  // Prefer MBOP: square then featureHorizontal; return first pair (deduped)
  if (m?.loyaltyOffers) {
    const los = Array.isArray(m.loyaltyOffers) ? m.loyaltyOffers : [m.loyaltyOffers];
    const out: any[] = [];
    for (const lo of los) {
      const imgs = Array.isArray(lo?.imageData) ? lo.imageData : lo?.imageData ? [lo.imageData] : [];
      for (const d of imgs) {
        const urlBase = checkChinaLocale(locale) ? imageDomainCN : imageDomain;
        const alt = d?.alternateDescription;
        const s = prependDomainIfMissing(d?.imageUrls?.square, urlBase),
          f = prependDomainIfMissing(d?.imageUrls?.featureHorizontal, urlBase);
        if (s) out.push({ altImageUrl: null, alternateText: alt, url: String(s) });
        if (f && !out.some(x => x?.url === f)) out.push({ altImageUrl: null, alternateText: alt, url: String(f) });
        if (out.length >= 2) {
          const seen = new Set<string>();
          return out.filter(x => (x?.url && !seen.has(x?.url) ? (seen.add(x?.url), true) : false)).slice(0, 2);
        }
      }
    }
  }
  // CTS fallback: pass-through, dedupe by url
  const imgs = Array.isArray(m?.images) ? m.images : [];
  const mapped = imgs
    .filter((i: any) => !!i?.url)
    .map((i: any) => ({
      altImageUrl: i?.altImageUrl ?? null,
      alternateText: i?.alternateText ?? '',
      url: String(i?.url),
    }));
  const seen = new Set<string>();
  return mapped.filter((x: { url: string }) => (x?.url && !seen.has(x?.url) ? (seen.add(x?.url), true) : false));
};

const promotionsToEdges = (cp: any, root: any, locale: string) => {
  const promos = cp?.promotions || [];
  const media = cp?.media || [];
  const offerIdx = buildOfferDescIndex(root);
  const promoDescIdx = buildPromoDescIndex(cp);

  return promos.map((p: any) => {
    const m = media.find((x: any) => String(x?.id || '').endsWith(`${p?.id}.media`));
    const primaryOfferId = p?.offers?.[0]?.id;
    const offerDescs = primaryOfferId ? offerIdx[primaryOfferId] : undefined;
    const descName = p?.id && promoDescIdx[p.id] != null ? promoDescIdx[p.id] : pickPrimary(offerDescs) ?? null;

    return {
      node: {
        id: p?.id,
        hurdleBalance: p?.hurdleBalance ?? 0,
        hurdleAchieved: p?.hurdleAchieved ?? 0,
        descriptions: { name: descName },
        media: { images: imagesFromMedia(m, locale) },
        name: p?.name,
        state: { code: p?.state?.code, description: p?.state?.description },
        earningLimited: !!p?.earningLimited,
        earnings: (p?.earnings || []).map((e: any) => ({
          quantity: e?.quantity,
          description: e?.description,
          type: { code: e?.type?.code, description: e?.type?.description },
        })),
        offers: (p?.offers || []).map((o: any) => ({
          hurdle: {
            hurdleType: { code: o?.hurdleType?.code, description: o?.hurdleType?.description },
            endDate: o?.endDate,
            maximumHurdle: o?.maximumHurdle,
            startDate: o?.startDate,
          },
          descriptions: { name: o?.name || '', descriptions: offerIdx[o?.id] || [] },
          id: o?.id,
        })),
      },
    };
  });
};

// Build the template with a closure that captures locale
const template = (locale: string) => ({
  'data?.marketing?.promotions?.customerPromotions': {
    key: 'data?.loyaltyPromotions?.edges',
    transform: (cp: any, root: any) => promotionsToEdges(cp, root, locale),
  },
});

export const mapPromotionsToLegacy = (response: any, locale: string) => {
  return mapper(response, template(locale));
};
